import { OutputConfig } from './OutputConfig.js';
import { BrowserHandler } from './BrowserHandler.js';

import { Conference } from "./Conference/Conference.js";
import { Lambdas } from './Lambdas.js';
import { Dispatcher } from './Dispatcher.js';
import { MediaLifecycle } from './MediaLifecycle.js';
import { ScreenWakeLock } from './ScreenWakeLock.js';
import { UserPrefs } from './UserPrefs.js';

//Peers
import { Peers_Handler } from "./Peers.js";
import { Logger } from './Logger.js';

import './styles.css'
// -- named ErrorPage on purpose: importing this as `Error` used to shadow the global Error
// -- constructor, so the intentional `throw new Error(...)` calls below threw Svelte component
// -- instances whose .message was undefined -- every page-load failure logged with no message
import ErrorPage from './UI/Components/Error.svelte';
import App from "./UI/Components/App.svelte"
import Loader from "./UI/Components/Loader.svelte"

let is_closing = false;
const cleanup = () => {
    if (!is_closing){
        is_closing = true;
        Conference.cleanUp();
        Dispatcher.cleanUpDecoderThread();
        Peers_Handler.clear();
    }
};

window.addEventListener('beforeunload', cleanup);
MediaLifecycle.init();
ScreenWakeLock.init();

// ---- Logging v2 page lifecycle ----------------------------------------------------------
// -- these answer "why did this session end" (user reload vs backgrounded-tab discard vs
// -- crash) and "was this load a refresh" -- the questions session forensics kept hitting.
// -- All are one-shot or transition events; none fire at a sustained rate. See LOGGING.md.

window.addEventListener('pagehide', e => {
    Logger.log("Page Load", "info", "Page Unloading", {
        persisted: e.persisted === true,                       // true -> going to bfcache, may come back
        dur_s: Math.round(performance.now() / 1000),
        has_joined: OutputConfig.has_joined_session === true,
        visibility: document.visibilityState,
    });
    Logger.flush(true);
});

document.addEventListener('visibilitychange', () => {

		Logger.log("Page Load", "info", "Visibility Changed", {
        state: document.visibilityState,
        dur_s: Math.round(performance.now() / 1000),
    });

    // -- hidden is the last reliable moment before a mobile background kill: push all queues out
    if (document.visibilityState === "hidden"){
        Logger.flush();
        Dispatcher.flushWorkerLogs();
    }
});

// -- Chrome fires freeze before discarding a background tab (the suspected Android kill path);
// -- unknown-event listeners are harmless elsewhere. No explicit flush needed: entries enqueued
// -- while hidden beacon out immediately (see Logger._enqueue) -- a frozen page's timers never
// -- run, so a queued-only marker would die with the discard.
// // // ?? 
document.addEventListener('freeze', () => Logger.log("Page Load", "info", "Page Frozen", {dur_s: Math.round(performance.now() / 1000)}));
document.addEventListener('resume', () => Logger.log("Page Load", "info", "Page Resumed", {dur_s: Math.round(performance.now() / 1000)}));

// -- Network Information API (Chromium only; undefined on WebKit) -- tests the "mobile network
// -- handoff killed the session" theory. Chromium re-estimates rtt/downlink continuously and
// -- fires 'change' for every re-bucket, so ONLY type/effectiveType transitions (the actual
// -- handoff signal) are logged; rtt/downlink ride along as context.
// -- Prolly delete
let _last_net = `${navigator.connection?.type ?? ""}/${navigator.connection?.effectiveType ?? ""}`;
navigator.connection?.addEventListener?.('change', () => {
		console.warn("Network change signal from navigator API")
		const c = navigator.connection;
    const net = `${c.type ?? ""}/${c.effectiveType ?? ""}`;
    if (net === _last_net) return;
    _last_net = net;
    Logger.log("Page Load", "info", "Network Changed", {type: c.type, effective_type: c.effectiveType, downlink: c.downlink, rtt: c.rtt});
});

// -- back online: a MOQ session stuck on the WebSocket fallback retries WebTransport (the decoder
// -- worker hosts the relays but gets no online event of its own)
window.addEventListener('online', () => Dispatcher.networkOnline());

// -- Generic remote logging for errors and unhandles rejections anywhere on the main thread. We can add these to workers with the self namesapce ("self.addEventListener")
let _uncaught = 0;
window.addEventListener('error', e => {
    if (_uncaught++ >= 5) return;
    Logger.log("Errors", "error", "Uncaught Error", {error: e.message, source: `${e.filename ?? ""}:${e.lineno ?? ""}`});
});
window.addEventListener('unhandledrejection', e => {
    if (_uncaught++ >= 5) return;
    Logger.log("Errors", "error", "Unhandled Rejection", {error: e.reason?.message ?? String(e.reason)});
});
// -----------------------------------------------------------------------------------------

window.addEventListener('load', async e => {

    const loader = new Loader({
        target: document.getElementById('Root'),
        props: {fullPage: true}
    })

    try {

        const initObj = await Lambdas.initWebClient_v2();
        if (initObj.error){
            // -- href is what was sent to the lambda; invisible characters and appended
            //    query params show up percent-encoded here
            Logger.log("Errors", "error", "Init URL rejected", {href: window.location.href, error: initObj.error});
            if (initObj.error === "Not a valid url" || initObj.error === "No Valid Room or Other Room Name Error"){
                new ErrorPage({
                    target: document.getElementById('Root'),
                    props: {
                        type: "url",
                        data: initObj.error
                    }
                });
            }

            throw new Error(initObj.error);
        }

        const browser = BrowserHandler.detect(initObj.session.user_type);
        if (!browser || !browser.isSupported) {
            new ErrorPage({
                target: document.getElementById('Root'),
                props: {
                    type: "browser",
                    data: {
                        browser: browser.name,
                        version: browser.version
                    }
                }
            });

            throw new Error(`Browser not supported. Current browser: ${browser.name} ${browser.version}`);
        }

        // -- this checks the auths
        const parseResult = OutputConfig.parseInitObject(initObj, browser);
        if (parseResult.status === "error"){
            if (parseResult.data === "[PL CLIENT ** ERROR] No Active Sessions"){
                new ErrorPage({
                    target: document.getElementById('Root'),
                    props: {
                        type: "auth",
                        data: parseResult.data
                    }
                });
            }
            throw new Error(parseResult.data);
        }

        Logger.room = OutputConfig.session.room_name;
        Logger.user_type = OutputConfig.session.self_type;

        // -- nav_type answers "was this load a refresh"; was_discarded answers "did the browser
        // -- kill and restore this tab" (Chrome). Both were previously unknowable from logs.
        const nav_entry = performance.getEntriesByType?.("navigation")?.[0];
        const hints = await BrowserHandler.client_hints();
        Logger.log("Page Load", "info", "Browser Init", {
            ...browser,
            ...hints,
            nav_type: nav_entry?.type,                       // "navigate" | "reload" | "back_forward"
            was_discarded: document.wasDiscarded === true,
            visibility: document.visibilityState,
            effective_type: navigator.connection?.effectiveType,
            downlink: navigator.connection?.downlink,
            rtt: navigator.connection?.rtt,
            cross_origin_isolated: window.crossOriginIsolated,
            shared_array_buffer: typeof SharedArrayBuffer,
        });

        // -- the decoder needs SharedArrayBuffer, which only a cross-origin isolated page gets (our
        // -- COOP/COEP headers). Something stripped them: say so instead of a Join that never loads
        if (typeof SharedArrayBuffer === "undefined"){
            new ErrorPage({
                target: document.getElementById('Root'),
                props: {type: "isolation"}
            });

            Logger.log("Page Load", "error", "Isolation Probe", await BrowserHandler.isolation_probe());
            throw new Error(`No SharedArrayBuffer (crossOriginIsolated: ${window.crossOriginIsolated})`);
        }

        await UserPrefs.load();
        if (UserPrefs.prefs?.name) Logger.user_name = UserPrefs.prefs.name;

        // -- NOTE: sets use_expanded_io to true for eng peers
        Peers_Handler.create_self(OutputConfig.session.room_name, OutputConfig.session.self_type);

        loader.$destroy();
        const a = new App({
            target: document.getElementById('Root'),
            props: {
                self_type: OutputConfig.session.self_type,
                room_name: OutputConfig.session.room_name,
                conf_disabled: OutputConfig.session.disable_conf,
            }
        })
    }

    catch(error) {
        loader.$destroy();
        // -- error level flushes immediately, so this survives even an immediate tab close
        Logger.log("Page Load", "error", "Init Failed", {
            error: error?.message ?? String(error),
            stack: error?.stack?.slice?.(0, 600),
        });

        // -- url/browser/auth failures mount their own page before throwing; anything else (lambda
        // -- unreachable, bad response, a throw further down) gets the generic one instead of a blank page
        const root = document.getElementById('Root');
        if (!root.childElementCount)
            new ErrorPage({target: root, props: {type: "unknown", data: error?.message ?? String(error)}});
        return
    }
})
